/**
 * Planilla general de inscripción en Excel — una hoja con los bloques en el
 * mismo orden del formato impreso, pero con formato de verdad: banda de
 * encabezado, cabeceras fijas, cebra, bordes finos y ancho de impresión.
 *
 * Va en Excel y no sólo en PDF porque la Asociación sigue consolidando los
 * clubes a mano: del PDF hay que volver a teclear 25 nombres, del Excel se
 * copia el bloque y se pega.
 *
 * Usa ExcelJS y no el `xlsx` del resto del proyecto por una razón concreta:
 * el build community de SheetJS DESCARTA los estilos al escribir (negritas,
 * rellenos y bordes son de la versión de pago), así que con él la planilla
 * salía en texto plano. ExcelJS se carga con `import()` dinámico para que
 * sus ~600KB no entren al bundle de quien nunca genera una planilla.
 */
import { planillaFilename, type PlanillaData } from "./planillaInscripcion";

// Mismo morado que el PDF y que la app.
const MORADO = "FF5B21B6";
const MORADO_CLARO = "FFEDE9FE";
const GRIS = "FFF1F5F9";
const CEBRA = "FFFAFAFC";
const RAYA = "FFE2E8F0";
const TEXTO = "FF0F172A";
const APAGADO = "FF64748B";
const FUENTE = "Calibri";

/** Toda celda pasa por aquí: una fuente sin `name` la resuelve cada Excel a
 *  su manera y la planilla se ve distinta en cada computador. */
const f = (o: {
  bold?: boolean;
  size?: number;
  color?: { argb: string };
}) => ({ name: FUENTE, ...o });

type Col = { title: string; width: number; align?: "left" | "center" };

const COLS: Col[] = [
  { title: "N°", width: 5, align: "center" },
  { title: "Nombres y apellidos", width: 34 },
  { title: "Documento", width: 20 },
  { title: "Nacimiento", width: 13, align: "center" },
  { title: "Edad", width: 7, align: "center" },
  { title: "Sexo", width: 7, align: "center" },
  { title: "RH", width: 6, align: "center" },
  { title: "EPS / Seguro", width: 20 },
  { title: "Teléfono", width: 14 },
  { title: "Cargo / Entrada", width: 26 },
  { title: "Bautiz.", width: 9, align: "center" },
];
const LAST = COLS.length; // 1-based: última columna de la hoja

export async function downloadPlanillaXlsx(data: PlanillaData): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;
  const { header, sections, totals } = data;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Insignia";
  wb.created = new Date();
  const ws = wb.addWorksheet("Planilla", {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
  });
  ws.columns = COLS.map((c) => ({ width: c.width }));

  const colLetter = (n: number) => ws.getColumn(n).letter;
  const span = (row: number, from = 1, to = LAST) =>
    `${colLetter(from)}${row}:${colLetter(to)}${row}`;

  // ════════ BANDA DEL ENCABEZADO ════════
  const titulo = ws.addRow(["PLANILLA GENERAL DE INSCRIPCIÓN"]);
  titulo.height = 26;
  ws.mergeCells(span(titulo.number));
  Object.assign(titulo.getCell(1), {
    font: f({ bold: true, size: 15, color: { argb: "FFFFFFFF" } }),
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: MORADO } },
    alignment: { vertical: "middle", horizontal: "left", indent: 1 },
  });

  const sub = ws.addRow([
    [header.eventName, header.eventDates].filter(Boolean).join("   ·   "),
  ]);
  sub.height = 18;
  ws.mergeCells(span(sub.number));
  Object.assign(sub.getCell(1), {
    font: f({ size: 10, color: { argb: "FFFFFFFF" } }),
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: MORADO } },
    alignment: { vertical: "middle", horizontal: "left", indent: 1 },
  });

  const club = ws.addRow([`${header.clubName}   ·   ${totals.total} inscritos`]);
  club.height = 22;
  ws.mergeCells(span(club.number));
  Object.assign(club.getCell(1), {
    font: f({ bold: true, size: 13, color: { argb: MORADO } }),
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: MORADO_CLARO } },
    alignment: { vertical: "middle", horizontal: "left", indent: 1 },
  });
  ws.addRow([]);

  // ════════ A. DATOS DEL CLUB ════════
  //
  // Etiqueta y valor van en la MISMA celda (texto enriquecido, dos tipos),
  // sobre rangos combinados que respetan los anchos de la tabla. Puesto un
  // campo por columna, "IGLESIA" caía en la columna de "N°" (5 caracteres)
  // y "Iglesia Central de Bucaramanga" se leía "Iglesi".
  const filasInfo: [string, string, number, number][][] = [
    [
      ["IGLESIA", header.churchName, 1, 3],
      ["DISTRITO", header.districtName, 4, 6],
      ["ZONA", header.zone, 7, 8],
      ["PASTOR ASISTENTE", header.assistantPastor, 9, 11],
    ],
    [
      ["MINISTRO", header.minister, 1, 3],
      ["DIRECTOR(A)", header.director, 4, 8],
      ["TELÉFONO", header.contactPhone, 9, 11],
    ],
  ];
  for (const campos of filasInfo) {
    const fila = ws.addRow([]);
    fila.height = 26;
    for (let i = 1; i <= LAST; i += 1) {
      fila.getCell(i).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: GRIS },
      };
    }
    for (const [etiqueta, valor, desde, hasta] of campos) {
      ws.mergeCells(span(fila.number, desde, hasta));
      const c = fila.getCell(desde);
      c.value = {
        richText: [
          {
            font: f({ bold: true, size: 7, color: { argb: APAGADO } }),
            text: `${etiqueta}\n`,
          },
          {
            font: f({ size: 10, color: { argb: TEXTO } }),
            text: valor || "—",
          },
        ],
      };
      c.alignment = { vertical: "middle", wrapText: true, indent: 1 };
    }
  }
  ws.addRow([]);

  // ════════ B…H. BLOQUES DE PERSONAS ════════
  const borde = { style: "thin" as const, color: { argb: RAYA } };

  for (const section of sections) {
    const nCols = section.showBaptized ? LAST : LAST - 1;

    const cab = ws.addRow([`${section.code}.  ${section.title}`]);
    cab.height = 19;
    ws.mergeCells(span(cab.number, 1, nCols));
    Object.assign(cab.getCell(1), {
      font: f({ bold: true,
        size: 10,
        color: { argb: section.showBaptized ? MORADO : TEXTO } }),
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: section.showBaptized ? MORADO_CLARO : GRIS },
      },
      alignment: { vertical: "middle", indent: 1 },
    });

    const th = ws.addRow(COLS.slice(0, nCols).map((c) => c.title));
    th.height = 16;
    th.eachCell((cell, i) => {
      cell.font = f({ bold: true, size: 8, color: { argb: APAGADO } });
      cell.alignment = {
        vertical: "middle",
        horizontal: COLS[i - 1]?.align ?? "left",
        indent: COLS[i - 1]?.align === "center" ? 0 : 1,
      };
      cell.border = {
        bottom: {
          style: "medium",
          color: { argb: section.showBaptized ? MORADO : APAGADO },
        },
      };
    });

    section.rows.forEach((r, idx) => {
      const datos: (string | number)[] = [
        r.order,
        r.fullName,
        [r.documentType, r.documentNumber].filter(Boolean).join(" "),
        r.birthDate,
        r.age,
        r.gender.slice(0, 1).toUpperCase(),
        r.bloodType,
        r.healthInsurance,
        r.phone,
        r.role,
      ];
      if (section.showBaptized) datos.push(r.baptized);
      const fila = ws.addRow(datos);
      fila.height = 15;
      fila.eachCell((cell, i) => {
        cell.font = f({ size: 9, color: { argb: TEXTO } });
        cell.alignment = {
          vertical: "middle",
          horizontal: COLS[i - 1]?.align ?? "left",
          indent: COLS[i - 1]?.align === "center" ? 0 : 1,
        };
        cell.border = { bottom: borde };
        if (idx % 2 === 1) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: CEBRA },
          };
        }
      });
    });
    ws.addRow([]);
  }

  // ════════ TOTALES ════════
  const tot = ws.addRow(["TOTALES"]);
  tot.height = 19;
  ws.mergeCells(span(tot.number));
  Object.assign(tot.getCell(1), {
    font: f({ bold: true, size: 10, color: { argb: "FFFFFFFF" } }),
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: MORADO } },
    alignment: { vertical: "middle", indent: 1 },
  });

  /** Etiqueta a la izquierda y cifra a la derecha, teñidas como los bloques. */
  const totalRow = (
    etiqueta: string,
    valor: number,
    fuerte: boolean,
    moneda = false,
  ) => {
    const r = ws.addRow([etiqueta]);
    r.height = 17;
    ws.mergeCells(span(r.number, 1, 2));
    for (let i = 1; i <= 3; i += 1) {
      r.getCell(i).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fuerte ? MORADO_CLARO : GRIS },
      };
    }
    Object.assign(r.getCell(1), {
      font: f({ bold: fuerte, size: fuerte ? 11 : 10, color: { argb: TEXTO } }),
      alignment: { vertical: "middle", indent: 1 },
    });
    const v = r.getCell(3);
    v.value = valor;
    if (moneda) v.numFmt = '"$" #,##0';
    v.font = f({
      bold: true,
      size: fuerte ? 12 : 10,
      color: { argb: fuerte ? MORADO : TEXTO },
    });
    v.alignment = { vertical: "middle", horizontal: "right", indent: 1 };
  };

  totalRow("Aventureros (con y sin uniforme)", totals.participants, false);
  totalRow(
    "Directiva, cocina, consejeros y acompañantes",
    totals.adults,
    false,
  );
  totalRow("TOTAL DE INSCRITOS", totals.total, true);
  totalRow("Valor total de las inscripciones", totals.amount, false, true);

  ws.addRow([]);
  ws.addRow([]);
  const firmas = ws.addRow([]);
  firmas.getCell(2).value = "Firma del director(a)";
  firmas.getCell(8).value = "Firma del pastor";
  for (const i of [2, 8]) {
    Object.assign(firmas.getCell(i), {
      font: f({ size: 9, color: { argb: APAGADO } }),
      border: { top: { style: "thin", color: { argb: APAGADO } } },
    });
  }

  // Pie de impresión: en una planilla de dos hojas, la segunda sin esto no
  // dice de qué club es.
  ws.headerFooter = {
    oddFooter: `&L&8${header.clubName}  ·  Generada en Insignia el ${header.generatedAt}&R&8Página &P de &N`,
  };

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${planillaFilename(header.clubName, header.eventName)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
